/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import React from 'react'
import { VatomIdentitySDK, VatomIdentitySDKFrame } from '@vatom/identity-sdk'

import Environment from './Environment'
import AppState from './AppState'

/**
 * Class that manages interactions with the identity SDK.
 */
export default new class IdentitySDK {

    /**
     * @private Reference to the identity SDK.
     * @type {VatomIdentitySDK}
     */
    _sdk = null

    /**
     * @private Reference to the identity SDK rendered in an iframe.
     * @type {VatomIdentitySDKFrame}
     */
    _frameSDK = null

    /** Constructor for the identity SDK */
    constructor() {
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.delete('token')

        const paramsLogin = new URLSearchParams('?' + searchParams.toString())
        paramsLogin.append('from_login', '1')

        const paramsLogout = new URLSearchParams('?' + searchParams.toString())
        paramsLogout.append('from_logout', '1')

        const loginCallbackUri = `${window.location.pathname}${paramsLogin.size > 0 ? '?' + paramsLogin.toString() : ''}`
        const logoutCallbackUri = '/logout'

        // Initialize SDK
        this._sdk = new VatomIdentitySDK(
            Environment.current.data.vatomClientID,
            {
                authority: Environment.current.data.servers.id,
                loginCallbackUri: loginCallbackUri,
                logoutCallbackUri: logoutCallbackUri,
            }
        )

        // Trigger callbacks for the current URL
        this.onCallbacks()
    }

    /** @returns {Promise<boolean>} `true` if the user is logged in, `false` otherwise. */
    async isLoggedIn() {
        let result = false

        try {
            result = await this._sdk.isLoggedIn()
        } catch (err) { }

        return result
    }

    /** @returns {Promise<boolean>} Execute the callbacks, returning the result. */
    async onCallbacks() {
        let result = false
        try {
            result = await this._sdk.onCallbacks()
        } catch (err) {
            console.error('[IdentitySDK] Something went wrong when executing "onCallbacks".', err)
        }

        // Done
        return result
    }

    /** @returns {Promise<boolean>} `true` if the profile is complete, `false` otherwise. */
    async isProfileComplete(businessId) {
        return await this._sdk.getIsProfileComplete(businessId)
    }

    /** @returns Profile information for the logged in user */
    async getProfile() {
        return await this._sdk.getProfile()
    }

    /** Performs the initial login. */
    async initialLogin() {
        const isLoggedIn = await this.isLoggedIn()

        // User is logged in, so get their profile
        if (isLoggedIn) {
            return await this.getProfile()
        }

        // Log in as guest (user can always log in with actual account later)
        await this._sdk.loginGuest()
        return await this.getProfile()
    }

    /** Logs the user in, redirecting to the appropriate page. */
    async login() {
        const isLoggedIn = await this.isLoggedIn()
        if (isLoggedIn) {
            return
        }

        await this._sdk.login()
    }

    /** Logs the current user out. */
    async logout() {
        const isLoggedIn = await this.isLoggedIn()
        if (!isLoggedIn) {
            return
        }

        localStorage.setItem('logout-from-space', AppState.spaceDetails?.alias || AppState.serverID)
        await this._sdk.logout()
    }

    /** @returns Token object. */
    getTokenObject() {
        return this._sdk.getStoredTokenResponse()
    }

    /**
     * Sets the stored token object.
     * @param {object} token Token object.
     */
    setTokenObject(token) {
        this._sdk.storeTokenResponse(token)
    }

    /** @returns Access token for the current user. */
    async getAccessToken() {
        const token = await this._sdk.getAccessToken()
        return token
    }

    /** @returns {string | null} Profile sub, if it exists. */
    getProfileSub() {
        return this.getTokenObject()?.profile?.sub || null
    }

    /**
     * Attaches the identity SDK to an element.
     * @param {HTMLElement} element Element to attach the frame to.
     * @param {() => void} onLogin Function to execute when the user has logged in.
     * @returns {boolean} `true` if we have successfully attached the SDK, `false` otherwise.
     */
    attachToElement(element, onLogin, businessId) {
        if (!element) {
            return false
        }

        try {
            this._frameSDK = new VatomIdentitySDKFrame(element, Environment.current.data.vatomClientID, onLogin, { authority: Environment.current.data.servers.id, extraAuthorizeParams: { "business-id": businessId }})
        } catch (err) {
            console.error('[IdentitySDK] Unable to assign SDK to element.', err)
            return false
        }

        return true
    }

}

/**
 * Screen used to sign into a profile.
 */
export class SignInScreen extends React.PureComponent {

    /**
     * @private Reference to the identity SDK rendered in an iframe.
     * @type {VatomIdentitySDKFrame}
     */
    _frameSDK = null

    /** `true` if we are on a mobile device, `false` otherwise */
    get isMobile() {
        return window.innerWidth < 850
    }

    /**
     * Attaches the identity SDK to an element.
     * @param {HTMLElement} element Element to attach the frame to.
     * @param {() => void} onLogin Function to execute when the user has logged in.
     */
    attachToElement(element, onLogin, businessId) {
        if (this._frameSDK) {
            return
        }

        if (!element) {
            return
        }

        try {
            this._frameSDK = new VatomIdentitySDKFrame(element, Environment.current.data.vatomClientID, onLogin, { authority: Environment.current.data.servers.id, extraAuthorizeParams: { "business-id": businessId }})
        } catch (err) {
            console.error('[IdentitySDK] Unable to assign SDK to element.', err)
        }
    }

    /** Render UI */
    render() {
        return <div style={{ display: 'flex', flexDirection: 'column', color: '#B3B4B7', position: 'relative', width: '100%', height: '100%', padding: this.isMobile ? 20 : 40, boxSizing: 'border-box', overflow: 'auto' }}>
            {/* Close button */}
            <img draggable='false' src={require('../../images/close-grey.svg')} onClick={e => this.props.onClose(e)} style={{ position: 'absolute', top: this.isMobile ? 20 : 32, right: this.isMobile ? 20 : 32, width: this.isMobile ? 18 : 26, height: this.isMobile ? 18 : 26, cursor: 'pointer', zIndex: 1 }} />

            <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
                <div ref={r => this.attachToElement(r, this.props.onLogin, this.props.businessId)} style={{ position: 'relative', width: '100%', height: '100%', overflow: 'auto' }}></div>
            </div>
        </div>
    }

}
